import type { ComponentConfig } from "@intelligems/ig-types/dtos/components";
import type { SharedEmbeddedMod } from "@intelligems/ig-types/schemas/visual-builder/mod-schemas";
import { imageModHandler } from "@src/store/slices/experienceBuilder/basicMod/utils/imageModHandler";
import { injectionModHandler } from "@src/store/slices/experienceBuilder/basicMod/utils/injectionModHandler";
import { layoutModHandler } from "@src/store/slices/experienceBuilder/basicMod/utils/layoutModHandler";
import { textModHandler } from "@src/store/slices/experienceBuilder/basicMod/utils/textModHandler";
import { bundleOnsiteUpsellModHandler } from "@src/store/slices/experienceBuilder/component/utils/bundleOnsiteUpsellModHandler";
import { bannerComponentHandler } from "@src/store/slices/experienceBuilder/component/utils/bannerComponentHandler";
import { genericComponentHandler } from "@src/store/slices/experienceBuilder/component/utils/genericComponentHandler";
import { imageComponentHandler } from "@src/store/slices/experienceBuilder/component/utils/imageComponentHandler";
import { onsiteUpsellModHandler } from "@src/store/slices/experienceBuilder/component/utils/onsiteUpsellModHandler";
import { textComponentHandler } from "@src/store/slices/experienceBuilder/component/utils/textComponentHandler";
import type {
  EmbeddedMod,
  ModHandler,
} from "@src/store/slices/experienceBuilder/types";

const modHandlerRegistry = {
  text: textModHandler,
  image: imageModHandler,
  layout: layoutModHandler,
  injection: injectionModHandler,
  textComponent: textComponentHandler,
  imageComponent: imageComponentHandler,
  bannerComponent: bannerComponentHandler,
  genericComponent: genericComponentHandler,
  onsiteUpsell: onsiteUpsellModHandler,
  bundleOnsiteUpsell: bundleOnsiteUpsellModHandler,
};

export function getModHandler(mod: EmbeddedMod) {
  return modHandlerRegistry[mod.type] as ModHandler<EmbeddedMod>;
}

// Component DTOs dispatch by `config.type` (DTO -> mod), mirroring how
// `getModHandler` dispatches by `mod.type` (mod -> DTO). Exhaustive over
// `ComponentConfig["type"]`: adding a new component config is a compile error
// here until its handler is registered — never a silent drop.
const componentHandlerByConfigType: Record<
  ComponentConfig["type"],
  ModHandler<EmbeddedMod>
> = {
  onsiteUpsell: onsiteUpsellModHandler as ModHandler<EmbeddedMod>,
  bundleOnsiteUpsell: bundleOnsiteUpsellModHandler as ModHandler<EmbeddedMod>,
};

export function getComponentHandler(configType: ComponentConfig["type"]) {
  return componentHandlerByConfigType[configType];
}

export function isEquivalent(a: EmbeddedMod, b: EmbeddedMod): boolean {
  return getModHandler(a).isEquivalent(a, b);
}

export function modsToAiFormat(mods: EmbeddedMod[]): SharedEmbeddedMod[] {
  return mods.map((mod) => getModHandler(mod).toAiFormat(mod));
}

/**
 * Convert AI-format mods into internal full mods. The internal Redux
 * representation stays flat (with `variationId` per mod); the AI boundary
 * doesn't carry `variationId` per mod — grouping is structural in the AI's
 * `context.mods` Record, and the call targets the previewed variation.
 *
 * Each handler is responsible for restoring frontend-only meta via
 * `applyAiMeta(base, aiMod, context)`. We don't mutate the produced mod
 * here — the handler owns the conversion.
 *
 * Existing-mod lookup is scoped to the same variation: a stray id from
 * another variation is treated as "no existing match", so no other
 * variation's meta can be merged in here. That is this layer's guarantee
 * only — the id itself still reaches the reducer, which upserts by id
 * alone; `update_mods` rejects foreign ids before they get this far.
 */
export function aiModsToFullFormat(
  aiMods: SharedEmbeddedMod[],
  existingMods: EmbeddedMod[],
  createdAtUrl: string | undefined,
  variationId: string
): EmbeddedMod[] {
  const existingById = new Map(
    existingMods
      .filter((m) => m.id && m.variationId === variationId)
      .map((m) => [m.id, m])
  );
  return aiMods.map((aiMod) => {
    const handler = modHandlerRegistry[aiMod.type] as ModHandler<EmbeddedMod>;
    const existing = aiMod.id ? existingById.get(aiMod.id) : undefined;
    return handler.fromAiFormat(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      aiMod as any,
      { variationId, existing, createdAtUrl }
    );
  });
}
